import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EmailCaptureConfig } from '@/types/vsleads/emailCaptureTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SliderNumericField } from '@/components/ui/numeric-input';
import { CompactColorPicker } from '@/components/ui/compact-color-picker';
import { Switch } from '@/components/ui/switch';
import { Eye, EyeOff } from 'lucide-react';

interface VisualStyleTabProps {
  form: UseFormReturn<EmailCaptureConfig>;
  isCapturePassword?: boolean;
}

export function VisualStyleTab({ form, isCapturePassword = false }: VisualStyleTabProps) {
  useLanguage();
  const style = form.watch('style');
  const overlayEnabled = form.watch('style.overlay.darkness') > 0;
  const skipEnabled = form.watch('texts.skipEnabled');

  return (
    <div className="space-y-6">
      {/* Cores e Estilos */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#101828]">{translateInterfaceText("Cores e Estilos")}</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {translateInterfaceText("Personalize as cores dos elementos do formulário")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <CompactColorPicker
              label={translateInterfaceText("Background do Vídeo")}
              color={style.container.background.value}
              onChange={(color) =>
                form.setValue('style.container.background.value', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={isCapturePassword ? translateInterfaceText("Campo de Senha") : translateInterfaceText("Formulário")}
              color={style.fields.background}
              onChange={(color) =>
                form.setValue('style.fields.background', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Background Botão")}
              color={style.button.normal}
              onChange={(color) =>
                form.setValue('style.button.normal', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Texto do Botão")}
              color={style.button.text}
              onChange={(color) =>
                form.setValue('style.button.text', color, { shouldDirty: true })
              }
            />

            <CompactColorPicker
              label={translateInterfaceText("Título")}
              color={style.text.title}
              onChange={(color) =>
                form.setValue('style.text.title', color, { shouldDirty: true })
              }
            />

            {!isCapturePassword && (
              <CompactColorPicker
                label={translateInterfaceText("Rodapé")}
                color={style.text.footer}
                onChange={(color) =>
                  form.setValue('style.text.footer', color, { shouldDirty: true })
                }
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recursos de Interação */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#101828]">{translateInterfaceText("Recursos de Interação")}</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {translateInterfaceText("Configure opções visuais e de comportamento do formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Background (Overlay) */}
          <div className="bg-[#F7F8FE] rounded-xl p-4 space-y-3 border border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye
                  className={`w-5 h-5 transition-colors duration-200 ${
                    overlayEnabled ? 'text-[#2D6CF6]' : 'text-[#94A3B8]'
                  }`}
                  strokeWidth={2}
                />
                <span className="text-sm font-medium text-[#1A1A1A]">
                  {translateInterfaceText("Background")}</span>
              </div>
              <Switch
                checked={overlayEnabled}
                onCheckedChange={(checked) => {
                  form.setValue('style.overlay.darkness', checked ? 0.7 : 0, { shouldDirty: true });
                }}
                className="data-[state=checked]:bg-[#3072FF]"
              />
            </div>
            <p className="text-[13px] text-[#6B7280] leading-relaxed pl-7">
              {translateInterfaceText("Adiciona uma camada colorida sobre o vídeo quando o formulário aparece. A cor é definida em \"Background do Vídeo\" acima")}</p>
          </div>

          {/* Pular Dados - Ocultar quando for captura por senha */}
          {!isCapturePassword && (
            <div className="bg-[#F7F8FE] rounded-xl p-4 space-y-3 border border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <EyeOff
                    className={`w-5 h-5 transition-colors duration-200 ${
                      skipEnabled ? 'text-[#2D6CF6]' : 'text-[#94A3B8]'
                    }`}
                    strokeWidth={2}
                  />
                  <span className="text-sm font-medium text-[#1A1A1A]">
                    {translateInterfaceText("Pular Dados")}</span>
                </div>
                <Switch
                  checked={skipEnabled}
                  onCheckedChange={(checked) => {
                    form.setValue('texts.skipEnabled', checked, { shouldDirty: true });
                  }}
                  className="data-[state=checked]:bg-[#3072FF]"
                />
              </div>
              <p className="text-[13px] text-[#6B7280] leading-relaxed pl-7">
                {translateInterfaceText("Exibe um botão permitindo que o usuário pule o preenchimento do formulário e continue assistindo o vídeo")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aparência do Formulário */}
      {/* <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#101828]">Aparência do Formulário</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            Ajuste bordas e cantos arredondados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SliderNumericField
            label="Raio de Borda dos Campos"
            value={style.fields.radius}
            onChange={(value) =>
              form.setValue('style.fields.radius', value, { shouldDirty: true })
            }
            min={0}
            max={16}
            step={2}
            suffix="px"
          />

          <SliderNumericField
            label="Raio de Borda do Botão"
            value={style.button.radius}
            onChange={(value) =>
              form.setValue('style.button.radius', value, { shouldDirty: true })
            }
            min={0}
            max={16}
            step={2}
            suffix="px"
          />
        </CardContent>
      </Card> */}
    </div>
  );
}
